import { useCallback, useMemo, useState } from 'react';
import { Icon } from '@fuxi/eevee-icon';
import { DialogProps } from 'antd-mobile';
import { RobotExceptionValues } from '@/components/robot-exception-prompt/utils';

type UseDomDialogProps = {
  isConfirmTextDisabled?: boolean;
  isCancelTextDisabled?: boolean;
  onClickConfirm: VoidFunc;
  onClickCancel?: VoidFunc;
} & RobotExceptionValues;

const useDomDialog = (props: UseDomDialogProps) => {
  const [visible, setVisible] = useState(false);
  const {
    confirmText,
    cancelText,
    iconName,
    iconColor,
    title,
    content,
    onClickConfirm,
    onClickCancel,
    isCancelTextDisabled = false,
    isConfirmTextDisabled = false,
    ...restProps
  } = props || {};

  const actions: DialogProps['actions'] = useMemo(
    () =>
      cancelText
        ? [
            {
              key: 'cancel',
              text: cancelText,
              disabled: isCancelTextDisabled,
              onClick: () => onClickCancel?.(),
            },
            {
              key: 'confirm',
              text: confirmText,
              disabled: isConfirmTextDisabled,
              onClick: () => onClickConfirm?.(),
            },
          ]
        : [
            {
              key: 'confirm',
              text: confirmText,
              disabled: isConfirmTextDisabled,
              onClick: () => onClickConfirm?.(),
            },
          ],
    [cancelText, confirmText, isCancelTextDisabled, isConfirmTextDisabled, onClickCancel, onClickConfirm]
  );

  const formatTitle = useMemo(
    () =>
      iconName ? (
        <div>
          <Icon
            name={iconName}
            style={{ marginRight: '8px', color: iconColor, position: 'relative', top: '1px' }}
            size={24}
          />
          {title}
        </div>
      ) : (
        title
      ),
    [iconName, iconColor, title]
  );

  const toggle = useCallback((flag: boolean) => () => setVisible(flag), []);

  // 格式化内容，将字符串"<br />"更改为换行标签
  const formatContent = useMemo(() => {
    const splitContent = content?.split('<br />');
    return (
      <div>
        {splitContent?.map((item, index) =>
          index === 0 ? (
            <span key={index}>{item}</span>
          ) : (
            <span key={index}>
              <br />
              {item}
            </span>
          )
        )}
      </div>
    );
  }, [content]);

  return {
    show: toggle(true),
    hide: toggle(false),
    dialogProps: {
      title: formatTitle,
      content: formatContent,
      actions,
      visible,
      ...restProps,
    },
  };
};

export default useDomDialog;
